// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ChartCard
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS chart card used to visualize simple
 * analytics series such as trends, counts, and comparisons. The component
 * converts shared chart points into a lightweight bar-style presentation.
 */

import type { ChartDataPoint } from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
} from "@geenius/ui/solidjs-css";
import { type Component, createUniqueId, For } from "solid-js";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS chart card.
 *
 * @property title Section heading displayed above the chart body.
 * @property data Ordered data points rendered as bars.
 * @property type Optional chart mode metadata for consumers.
 * @property height Optional pixel height for the chart body.
 * @property class Optional class name merged into the card wrapper.
 */
export interface ChartCardProps {
  title: string;
  data: ChartDataPoint[];
  type?: "bar" | "line";
  height?: number;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS CSS chart card for a list of analytics datapoints.
 *
 * @param props Chart metadata and datapoints to visualize.
 * @returns A chart card element with proportional bars.
 */
export const ChartCard: Component<ChartCardProps> = (props) => {
  const summaryId = createUniqueId();
  const values = (): number[] =>
    props.data.map((point) =>
      Number.isFinite(point.value) ? Math.max(0, point.value) : 0,
    );
  const max = (): number => Math.max(...values(), 1);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader>
          <CardTitle class="gn-analytics-chart-card__title">
            {props.title}
          </CardTitle>
        </CardHeader>
        <CardContent class="gn-analytics-chart-card__content">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardHeader>
        <CardTitle class="gn-analytics-chart-card__title">
          {props.title}
        </CardTitle>
      </CardHeader>
      <CardContent class="gn-analytics-chart-card__content">
        <div
          class="gn-analytics-chart-card__body"
          data-analytics="chart-body"
          data-chart-type={props.type ?? "bar"}
          role="img"
          aria-label={`${props.title} ${props.type ?? "bar"} chart`}
          aria-describedby={summaryId}
          style={{ height: `${props.height ?? 200}px` }}
        >
          <ul
            id={summaryId}
            class="gn-analytics-a11y__sr-only"
            data-analytics="chart-data-list"
          >
            <For each={props.data}>
              {(point) => (
                <li>
                  {point.label}: {point.value}
                </li>
              )}
            </For>
          </ul>
          <div
            class="gn-analytics-chart-card__bars"
            data-analytics="chart-bars"
          >
            <For each={props.data}>
              {(point, index) => (
                <div
                  class="gn-analytics-chart-card__bar-group"
                  data-analytics="chart-bar-group"
                >
                  <Tooltip text={`${point.label}: ${point.value}`}>
                    <div
                      class={cx(
                        "gn-analytics-chart-card__bar",
                        props.type === "line" &&
                          "gn-analytics-chart-card__bar--line",
                      )}
                      data-analytics="chart-bar"
                      aria-label={`${point.label}: ${point.value}`}
                      role="img"
                      style={{
                        height: `${((values()[index()] ?? 0) / max()) * 100}%`,
                      }}
                    />
                  </Tooltip>
                  <span
                    class="gn-analytics-chart-card__label"
                    data-analytics="chart-label"
                  >
                    {point.label}
                  </span>
                </div>
              )}
            </For>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
